import React, { useState, useEffect } from 'react';
import { TrendingUp, Menu, X, Home, ShoppingBag, Settings, LogOut, Search } from 'lucide-react';
import authService from '../services/auth.service';

export default function Header({ currentPage = 'home', onNavigate, onLogout }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userInfo, setUserInfo] = useState({
    fullName: 'User',
    email: 'user@example.com',
    firstName: 'User'
  });

  useEffect(() => {
    // Get user info from authService
    const fullName = authService.getFullName() || 'User';
    const email = authService.getEmail() || 'user@example.com';
    
    // Extract first name from full name
    const firstName = fullName.split(' ')[0] || 'User';

    setUserInfo({
      fullName,
      email,
      firstName
    });
  }, []);

  const navItems = [
    { id: 'home', label: 'Dashboard', icon: Home },
    { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag },
  ];

  const handleNavigation = (pageId) => {
    if (onNavigate) {
      onNavigate(pageId);
    }
    setMobileMenuOpen(false);
    setShowUserMenu(false);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setMobileMenuOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  // Get initials for avatar
  const getInitials = () => {
    const names = userInfo.fullName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return userInfo.fullName.substring(0, 2).toUpperCase();
  };

  return (
    <>
      {/* Header - matches your home page dark theme */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <div 
              className="flex items-center gap-2 cursor-pointer group"
              onClick={() => handleNavigation('home')}
            >
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-1.5 rounded-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-semibold text-base md:text-lg">
                Utajiri Hub
              </span>
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPage === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </button>
                );
              })}

              {/* Desktop User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 bg-gray-800 rounded-full pl-1 pr-3 py-1 hover:bg-gray-700 transition-all"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">{getInitials()}</span>
                  </div>
                  {/* <span className="text-white text-sm font-medium">{userInfo.firstName}</span> */}
                </button>

                {/* Dropdown */}
                {showUserMenu && (
                  <>
                    {/* Backdrop for closing menu */}
                    <div 
                      className="fixed inset-0 z-10"
                      onClick={() => setShowUserMenu(false)}
                    />
                    
                    <div className="absolute right-0 mt-2 w-56 bg-gray-800 rounded-2xl shadow-xl border border-gray-700 py-2 z-20">
                      <div className="px-4 py-3 border-b border-gray-700">
                        <p className="text-white font-medium text-sm">{userInfo.fullName}</p>
                        <p className="text-gray-400 text-xs mt-0.5 truncate">{userInfo.email}</p>
                      </div>
                      
                      <button 
                        onClick={() => handleNavigation('settings')}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:bg-gray-700 transition-all text-left"
                      >
                        <Settings size={18} />
                        <span>Settings</span>
                      </button>
                      
                      <div className="border-t border-gray-700 mt-2 pt-2">
                        <button 
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-red-900/20 transition-all text-left"
                        >
                          <LogOut size={18} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile: Right side icons */}
            <div className="md:hidden flex items-center gap-1">
              <button className="text-gray-400 hover:text-white p-2 hover:bg-gray-800 rounded-full transition-all">
                <Search size={20} />
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-400 hover:text-white p-2 hover:bg-gray-800 rounded-full transition-all"
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/70 z-40 animate-in fade-in duration-200"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Slide-in Menu */}
      <div className={`md:hidden fixed top-0 right-0 h-full w-72 bg-gray-900 z-50 shadow-2xl transform transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Menu Header */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-b border-gray-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg">Menu</h3>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-gray-400 hover:text-white p-1.5 hover:bg-gray-800 rounded-full transition-all"
              >
                <X size={22} />
              </button>
            </div>
            
            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                <span className="text-white text-base font-semibold">{getInitials()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{userInfo.fullName}</p>
                <p className="text-gray-400 text-xs truncate">{userInfo.email}</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="flex-1 overflow-y-auto py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id)}
                  className={`w-full flex items-center gap-3 px-6 py-4 transition-all ${
                    isActive
                      ? 'bg-gray-800 text-white border-l-4 border-emerald-500'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}

            <div className="h-px bg-gray-800 my-2 mx-4" />

            <button 
              onClick={() => handleNavigation('settings')}
              className={`w-full flex items-center gap-3 px-6 py-4 transition-all ${
                currentPage === 'settings'
                  ? 'bg-gray-800 text-white border-l-4 border-emerald-500'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <Settings size={20} />
              <span className="font-medium">Settings</span>
            </button>

            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-6 py-4 text-red-400 hover:bg-red-900/20 transition-all"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}